import { useState, useEffect, useRef } from "react";
import { isInteractiveQuiz } from "../../utils/pollTypes.js";
import { updatePresentationState } from "../../hooks/usePresentationSync.js";

export default function useQuizPresentation(activeInteractiveQuizzes, session) {
  const [presMode, setPresMode] = useState(false);
  const [presSlide, setPresSlide] = useState(0);
  const [presTimeLeft, setPresTimeLeft] = useState(0);
  const presTimerRef = useRef(null);
  // Quiz chosen with a card's Start Presentation button. The ref is read synchronously by
  // the publish helpers below; the state lets the page render the same quiz.
  const presTargetIdRef = useRef(null);
  const [presTargetId, setPresTargetId] = useState(null);

  const getTargetPoll = () => {
    if (presTargetIdRef.current != null) {
      const all = [...(activeInteractiveQuizzes || []), ...((session?.polls || []).filter(isInteractiveQuiz))];
      const picked = all.find((p) => String(p.id) === String(presTargetIdRef.current));
      if (picked) return picked;
    }
    if (Array.isArray(activeInteractiveQuizzes) && activeInteractiveQuizzes.length) return activeInteractiveQuizzes[0];
    if (session?.polls) return session.polls.filter(isInteractiveQuiz)[0] || null;
    return null;
  };

  const getQuestionCount = () => {
    const poll = getTargetPoll();
    if (!poll?.data) return 1;
    const keys = Object.keys(poll.data).filter((k) => /^question\d+$/i.test(k));
    return keys.length || Number(poll.question_count) || 1;
  };

  // quiz (optional): the card's quiz. Without it (or when called as a plain onClick
  // handler, which passes an event) the first active quiz is used, as before.
  function startPresentation(quiz) {
    const picked = quiz && typeof quiz === "object" && !quiz.nativeEvent && quiz.id != null ? quiz : null;
    presTargetIdRef.current = picked ? picked.id : null;
    setPresTargetId(presTargetIdRef.current);
    setPresMode(true);
    setPresSlide(0);
    const target = picked || getTargetPoll();
    if (target && session?.sessionId) updatePresentationState(session.sessionId, target, 0);
  }

  function closePresentation() {
    setPresMode(false);
    setPresSlide(0);
    if (presTimerRef.current) clearInterval(presTimerRef.current);
    const target = getTargetPoll();
    if (target && session?.sessionId) updatePresentationState(session.sessionId, target, 999);
    else if (session?.sessionId) {
      (activeInteractiveQuizzes || []).forEach((k) => {
        if (k.id) updatePresentationState(session.sessionId, k, 999);
      });
    }
    presTargetIdRef.current = null;
    setPresTargetId(null);
  }

  function nextSlide() {
    const total = getQuestionCount();
    if (presSlide >= total + 1) { closePresentation(); return; }
    const next = presSlide + 1;
    setPresSlide(next);
    const target = getTargetPoll();
    if (target && session?.sessionId) updatePresentationState(session.sessionId, target, next);
  }

  function prevSlide() {
    if (presSlide <= 0) return;
    const prev = presSlide - 1;
    setPresSlide(prev);
    const target = getTargetPoll();
    if (target && session?.sessionId) updatePresentationState(session.sessionId, target, prev);
  }

  // Result slide only: publish whether users may see the result (showresult) while
  // the quiz stays open. Does not touch the quiz-result channel.
  function publishResultVisibility(showresult) {
    const target = getTargetPoll();
    if (target && session?.sessionId) updatePresentationState(session.sessionId, target, getQuestionCount() + 1, { showresult });
  }

  useEffect(() => {
    if (!presMode || presSlide === 0) {
      if (presTimerRef.current) clearInterval(presTimerRef.current);
      return;
    }
    const total = getQuestionCount();
    if (presSlide > total) return;
    const poll = getTargetPoll();
    const duration = Number(poll?.duration ?? poll?.timer) || 20;
    const endAt = Date.now() + duration * 1000;
    setPresTimeLeft(duration);
    if (presTimerRef.current) clearInterval(presTimerRef.current);
    presTimerRef.current = setInterval(() => {
      const left = Math.max(0, Math.ceil((endAt - Date.now()) / 1000));
      setPresTimeLeft(left);
      if (left <= 0) clearInterval(presTimerRef.current);
    }, 250);
    return () => { if (presTimerRef.current) clearInterval(presTimerRef.current); };
    // getQuestionCount/getTargetPoll read live state and are re-created every
    // render; the timer must restart only when the slide itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presMode, presSlide]);

  return { presMode, presSlide, presTimeLeft, presTargetId, startPresentation, closePresentation, nextSlide, prevSlide, publishResultVisibility };
}
